import React, { useCallback, useEffect, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import LoadingSpinner from '../components/LoadingSpinner';
import DeveloperNavigation, { REVENUE_ADMIN_EMAILS } from '../components/DeveloperNavigation';
import { billingAuthHeaders } from '../utils/billingApi';
import { getBackendBaseUrl } from '../utils/apiUrls';

const billingBase = `${getBackendBaseUrl()}/billing`;

const PAYMENT_USER_TYPE_OPTIONS = [
    { value: '', label: 'All' },
    { value: 'beta_tester', label: 'beta_tester' },
    { value: 'free_trial', label: 'free_trial' },
    { value: 'subscriber', label: 'subscriber' },
    { value: '__none__', label: '(none)' },
];

const thStyle = {
    textAlign: 'left',
    padding: '10px 12px',
    borderBottom: '1px solid #e5e7eb',
    background: '#f8fafc',
    position: 'sticky',
    top: 0,
    zIndex: 1,
};

const tdStyle = {
    padding: '10px 12px',
    borderBottom: '1px solid #f1f5f9',
    verticalAlign: 'top',
};

function centsToDisplay(cents) {
    return `$${((cents || 0) / 100).toFixed(2)}`;
}

function TierConfigPanel({ title, cfg, updateConfigField, saveConfig, centsToDisplay }) {
    if (!cfg) return null;
    return (
        <section style={{ border: '1px solid #e5e7eb', borderRadius: '8px', padding: '16px', background: '#fff' }}>
            <h3 style={{ marginTop: 0, fontSize: '1rem' }}>{title}</h3>
            <div style={{ display: 'grid', gap: '12px' }}>
                <label>Display name
                    <input
                        type="text"
                        value={cfg.display_name || ''}
                        onChange={(e) => updateConfigField(cfg.product_type, 'display_name', e.target.value)}
                        style={{ width: '100%' }}
                    />
                </label>
                <label>Monthly fee (cents)
                    <input
                        type="number"
                        value={cfg.monthly_fee_cents}
                        onChange={(e) => updateConfigField(cfg.product_type, 'monthly_fee_cents', +e.target.value)}
                        style={{ width: '100%' }}
                    />
                    <span style={{ fontSize: '0.85rem', color: '#64748b' }}>{centsToDisplay(cfg.monthly_fee_cents)}/mo</span>
                </label>
                <label>Transaction fee (cents)
                    <input
                        type="number"
                        value={cfg.transaction_fee_cents}
                        onChange={(e) => updateConfigField(cfg.product_type, 'transaction_fee_cents', +e.target.value)}
                        style={{ width: '100%' }}
                    />
                    <span style={{ fontSize: '0.85rem', color: '#64748b' }}>{centsToDisplay(cfg.transaction_fee_cents)}/deal</span>
                </label>
                {(cfg.stripe_price_id || cfg.stripe_transaction_fee_price_id) && (
                    <div style={{ fontSize: '0.85rem', color: '#64748b' }}>
                        {cfg.stripe_price_id && <div>Subscription price: {cfg.stripe_price_id}</div>}
                        {cfg.stripe_transaction_fee_price_id && (
                            <div>Transaction fee price: {cfg.stripe_transaction_fee_price_id}</div>
                        )}
                    </div>
                )}
                <button type="button" onClick={() => saveConfig(cfg)}>Save</button>
            </div>
        </section>
    );
}

function DeveloperRevenue() {
    const { user, isAuthenticated, loginWithRedirect, getAccessTokenSilently, getIdTokenClaims } = useAuth0();
    const navigate = useNavigate();

    const [pageSize, setPageSize] = useState(20);
    const [offset, setOffset] = useState(0);
    const [users, setUsers] = useState([]);
    const [referralSummaries, setReferralSummaries] = useState([]);
    const [totalCount, setTotalCount] = useState(0);
    const [productConfigs, setProductConfigs] = useState([]);
    const [defaultFreeDeals, setDefaultFreeDeals] = useState(1);
    const [defaultReferralTrialTransactions, setDefaultReferralTrialTransactions] = useState(5);
    const [loading, setLoading] = useState(true);
    const [tableLoading, setTableLoading] = useState(false);
    const [error, setError] = useState('');
    const [message, setMessage] = useState('');

    const [emailFilter, setEmailFilter] = useState('');
    const [paymentUserTypeFilter, setPaymentUserTypeFilter] = useState('');

    const [revenueUserQuery, setRevenueUserQuery] = useState('');
    const [revenueOverview, setRevenueOverview] = useState(null);
    const [revenueLoading, setRevenueLoading] = useState(false);
    const [revenueError, setRevenueError] = useState('');

    const isRevenueAdmin = REVENUE_ADMIN_EMAILS.includes((user?.email || '').toLowerCase());

    const buildParams = useCallback((resetOffset = true) => {
        const params = {
            email: user?.email || '',
            limit: pageSize,
            offset: resetOffset ? 0 : offset,
        };
        if (emailFilter.trim()) params.user_email = emailFilter.trim();
        if (paymentUserTypeFilter) params.payment_user_type = paymentUserTypeFilter;
        return params;
    }, [user?.email, pageSize, offset, emailFilter, paymentUserTypeFilter]);

    const fetchOverview = useCallback(async (reset = true, overrideParams = null) => {
        if (!isAuthenticated || !isRevenueAdmin) return;
        try {
            if (reset && users.length === 0) setLoading(true);
            else setTableLoading(true);
            setError('');

            const params = overrideParams || buildParams(reset);
            const headers = await billingAuthHeaders({ getAccessTokenSilently, getIdTokenClaims });
            const res = await axios.get(`${billingBase}/admin/overview`, { params, headers });

            const newUsers = res.data.users || [];
            setUsers(reset ? newUsers : [...users, ...newUsers]);
            setReferralSummaries(res.data.referral_summaries || []);
            setTotalCount(res.data.total_count ?? newUsers.length);
            setOffset((reset ? 0 : offset) + newUsers.length);
            const active = (res.data.product_configs || []).filter((c) => c.is_active !== false);
            setProductConfigs(active);
            setDefaultFreeDeals(res.data.default_free_trial_deals ?? 1);
            setDefaultReferralTrialTransactions(res.data.referral_trial_free_transactions_default ?? 5);
        } catch (e) {
            setError(e.response?.data?.error || e.message);
        } finally {
            setLoading(false);
            setTableLoading(false);
        }
    }, [isAuthenticated, isRevenueAdmin, buildParams, users, offset, getAccessTokenSilently, getIdTokenClaims]);

    useEffect(() => {
        if (!isAuthenticated) return;
        if (!isRevenueAdmin) {
            navigate('/');
            return;
        }
        fetchOverview(true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAuthenticated, isRevenueAdmin, pageSize]);

    const updateConfigField = (productType, field, value) => {
        setProductConfigs((prev) => prev.map((c) => (
            c.product_type === productType ? { ...c, [field]: value } : c
        )));
    };

    const saveConfig = async (cfg) => {
        if (!cfg) return;
        try {
            const headers = await billingAuthHeaders({ getAccessTokenSilently, getIdTokenClaims });
            const payload = {
                ...cfg,
                default_free_trial_deals: defaultFreeDeals,
                referral_trial_free_transactions_default: defaultReferralTrialTransactions,
            };
            const res = await axios.put(
                `${billingBase}/admin/product-config/${cfg.product_type}?email=${encodeURIComponent(user.email)}`,
                payload,
                { headers },
            );
            updateConfigField(cfg.product_type, 'stripe_price_id', res.data.stripe_price_id);
            updateConfigField(cfg.product_type, 'stripe_transaction_fee_price_id', res.data.stripe_transaction_fee_price_id);
            setMessage(`${cfg.display_name} saved${res.data.stripe_price_id ? ' (Stripe prices updated)' : ''}.`);
        } catch (e) {
            setMessage(e.response?.data?.error || e.message);
        }
    };

    const saveAllConfigs = async () => {
        for (const cfg of productConfigs) {
            // eslint-disable-next-line no-await-in-loop
            await saveConfig(cfg);
        }
    };

    const provisionStripe = async () => {
        try {
            const headers = await billingAuthHeaders({ getAccessTokenSilently, getIdTokenClaims });
            const res = await axios.post(
                `${billingBase}/admin/provision-stripe?email=${encodeURIComponent(user.email)}`,
                {},
                { headers },
            );
            const count = (res.data.provisioned || []).length;
            setMessage(count ? `Stripe provisioned for ${count} plan(s).` : 'Stripe provisioned.');
            fetchOverview(true);
        } catch (e) {
            setMessage(e.response?.data?.error || e.message);
        }
    };

    const saveUserTrialDeals = async (userId, allowed) => {
        try {
            const headers = await billingAuthHeaders({ getAccessTokenSilently, getIdTokenClaims });
            await axios.put(
                `${billingBase}/admin/user/${userId}/trial-deals?email=${encodeURIComponent(user.email)}`,
                { free_trial_deals_allowed: allowed === '' ? null : Number(allowed) },
                { headers },
            );
            setMessage('User trial deal allowance updated.');
            fetchOverview(true);
        } catch (e) {
            setMessage(e.response?.data?.error || e.message);
        }
    };

    const saveUserCourtesyDeals = async (userId, remaining) => {
        try {
            const headers = await billingAuthHeaders({ getAccessTokenSilently, getIdTokenClaims });
            await axios.put(
                `${billingBase}/admin/user/${userId}/courtesy-deals?email=${encodeURIComponent(user.email)}`,
                { courtesy_free_deals_remaining: remaining === '' ? 0 : Number(remaining) },
                { headers },
            );
            setMessage('Courtesy free deals updated.');
            fetchOverview(true);
        } catch (e) {
            setMessage(e.response?.data?.error || e.message);
        }
    };

    const isSubscriberUser = (row) => (row?.payment_user_type || '').toLowerCase() === 'subscriber';

    const loadUserRevenue = async () => {
        const query = revenueUserQuery.trim();
        if (!query) {
            setRevenueError('Enter a user ID or email address.');
            return;
        }
        try {
            setRevenueLoading(true);
            setRevenueError('');
            const headers = await billingAuthHeaders({ getAccessTokenSilently, getIdTokenClaims });
            const params = { email: user.email };
            if (/^\d+$/.test(query)) {
                params.user_id = query;
            } else {
                params.user_email = query;
            }
            const res = await axios.get(`${billingBase}/admin/user-revenue`, { params, headers });
            setRevenueOverview(res.data);
        } catch (e) {
            setRevenueOverview(null);
            setRevenueError(e.response?.data?.error || e.message);
        } finally {
            setRevenueLoading(false);
        }
    };

    const formatDate = (iso) => {
        if (!iso) return '—';
        try {
            return new Date(iso).toLocaleDateString();
        } catch {
            return iso;
        }
    };

    if (!isAuthenticated) {
        return (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh', flexDirection: 'column' }}>
                <p>Please log in</p>
                <button onClick={() => loginWithRedirect({ authorizationParams: { prompt: 'login' } })}>Log in</button>
            </div>
        );
    }

    if (!isRevenueAdmin) {
        return (
            <div style={{ padding: '24px' }}>
                <h2>Access Denied</h2>
                <p>This page is restricted to authorized KeyFlo revenue administrators.</p>
            </div>
        );
    }

    if (loading) return <LoadingSpinner />;

    const freeMonthlyCfg = productConfigs.find((c) => c.product_type === 'keyflo_tc_free_monthly');
    const lowMonthlyCfg = productConfigs.find((c) => c.product_type === 'keyflo_tc_low_monthly');

    return (
        <div style={{ padding: '24px' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
                <h2>Developer • Revenue</h2>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', position: 'relative' }}>
                    <button className="back-button" onClick={() => navigate('/')}>← Back to Contracts</button>
                    <label style={{ marginRight: '8px' }}>Page size:</label>
                    <select value={pageSize} onChange={(e) => setPageSize(parseInt(e.target.value, 10))}>
                        <option value={10}>10</option>
                        <option value={20}>20</option>
                        <option value={50}>50</option>
                    </select>
                    <DeveloperNavigation />
                </div>
            </div>

            {message && (
                <div style={{ marginBottom: '12px', padding: '8px 12px', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '4px' }}>
                    {message}
                </div>
            )}

            <div
                style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
                    gap: '20px',
                    marginBottom: '24px',
                    alignItems: 'start',
                }}
            >
                <section style={{ border: '1px solid #e5e7eb', borderRadius: '8px', padding: '16px', background: '#fff' }}>
                    <h3 style={{ marginTop: 0, fontSize: '1rem' }}>Global defaults</h3>
                    <label style={{ display: 'block' }}>
                        Default free trial deals
                        <input
                            type="number"
                            min={0}
                            value={defaultFreeDeals}
                            onChange={(e) => setDefaultFreeDeals(+e.target.value)}
                            style={{ width: '100%' }}
                        />
                    </label>
                    <label style={{ display: 'block', marginTop: '12px' }}>
                        Referral trial free transactions offered
                        <input
                            type="number"
                            min={0}
                            value={defaultReferralTrialTransactions}
                            onChange={(e) => setDefaultReferralTrialTransactions(+e.target.value)}
                            style={{ width: '100%' }}
                        />
                    </label>
                </section>
                <TierConfigPanel
                    title="keyflo_tc_free_monthly Tier"
                    cfg={freeMonthlyCfg}
                    updateConfigField={updateConfigField}
                    saveConfig={saveConfig}
                    centsToDisplay={centsToDisplay}
                />
                <TierConfigPanel
                    title="keyflo_tc_low_monthly Tier"
                    cfg={lowMonthlyCfg}
                    updateConfigField={updateConfigField}
                    saveConfig={saveConfig}
                    centsToDisplay={centsToDisplay}
                />
            </div>

            <div style={{ display: 'flex', gap: '8px', marginBottom: '32px' }}>
                <button type="button" onClick={saveAllConfigs}>Save all plans</button>
                <button type="button" onClick={provisionStripe}>Provision Stripe (all active plans)</button>
            </div>

            <h3>Revenue Summary by User</h3>
            <p style={{ color: '#64748b', fontSize: '0.9rem', marginTop: '-8px', maxWidth: '960px' }}>
                Fee status columns count <code>billing_transaction_fee</code> rows per user (created when a contract
                exists). <strong>Contracts</strong>, <strong>Listings (no contract)</strong>, and{' '}
                <strong>Buyer Agmts (no contract)</strong> show expected pipeline activity.{' '}
                <strong>Total Pipeline</strong> is the sum of those three. Fees can be{' '}
                <strong>cancelled</strong> (deal did not close) or <strong>waived</strong> (free trial, referral, or courtesy).{' '}
                Subscriber leftover trial deals are not spendable, so <strong>Deals Left</strong> is 0 and{' '}
                <strong>Allowed Deals</strong> is locked. Use <strong>Courtesy Left</strong> to grant ad hoc free closes.
            </p>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '12px', marginBottom: '12px', maxWidth: '600px' }}>
                <div>
                    <label style={{ display: 'block', fontSize: '0.85rem', marginBottom: '4px' }}>User Email</label>
                    <input value={emailFilter} onChange={(e) => setEmailFilter(e.target.value)} placeholder="email" style={{ width: '100%' }} />
                </div>
                <div>
                    <label style={{ display: 'block', fontSize: '0.85rem', marginBottom: '4px' }}>Payment User Type</label>
                    <select value={paymentUserTypeFilter} onChange={(e) => setPaymentUserTypeFilter(e.target.value)} style={{ width: '100%' }}>
                        {PAYMENT_USER_TYPE_OPTIONS.map((opt) => (
                            <option key={opt.value || 'all'} value={opt.value}>{opt.label}</option>
                        ))}
                    </select>
                </div>
            </div>

            <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
                <button onClick={() => { setOffset(0); fetchOverview(true); }}>Apply Filters</button>
                <button onClick={() => {
                    setEmailFilter('');
                    setPaymentUserTypeFilter('');
                    setOffset(0);
                    fetchOverview(true, {
                        email: user?.email || '',
                        limit: pageSize,
                        offset: 0,
                    });
                }}>Clear</button>
                <button onClick={() => fetchOverview(false)} disabled={users.length >= totalCount}>
                    Load More
                </button>
            </div>

            {tableLoading && <LoadingSpinner />}
            {error && (
                <div style={{ color: 'red', marginBottom: '12px' }}>{error}</div>
            )}

            <div style={{ marginBottom: '12px' }}>
                Showing {users.length} of {totalCount} users
            </div>

            <div style={{ overflowX: 'auto', background: '#ffffff', border: '1px solid #e5e7eb' }}>
                <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0 }}>
                    <thead>
                        <tr>
                            <th style={thStyle}>Email</th>
                            <th style={thStyle}>Plan</th>
                            <th style={thStyle}>Payment Type</th>
                            <th style={thStyle}>Deals Used</th>
                            <th style={thStyle}>Deals Left</th>
                            <th style={thStyle}>Allowed Deals</th>
                            <th style={thStyle}>Courtesy Left</th>
                            <th style={thStyle}>Contracts</th>
                            <th style={thStyle}>Listings (no contract)</th>
                            <th style={thStyle}>Buyer Agmts (no contract)</th>
                            <th style={thStyle}>Total Pipeline</th>
                            <th style={thStyle}>Tentative</th>
                            <th style={thStyle}>Recognized</th>
                            <th style={thStyle}>Scheduled</th>
                            <th style={thStyle}>Charged</th>
                            <th style={thStyle}>Cancelled</th>
                            <th style={thStyle}>Waived</th>
                            <th style={thStyle}>Billing Status</th>
                            <th style={thStyle}>Last 90d Charges</th>
                            <th style={thStyle}>Next Payment</th>
                        </tr>
                    </thead>
                    <tbody>
                        {users.map((u) => (
                            <tr key={u.id}>
                                <td style={tdStyle}>{u.email}</td>
                                <td style={tdStyle}>{u.product_type || '—'}</td>
                                <td style={tdStyle}>{u.payment_user_type || '—'}</td>
                                <td style={tdStyle}>{u.free_trial_deals_used ?? '—'}</td>
                                <td style={tdStyle}>
                                    {isSubscriberUser(u) ? 0 : (u.free_trial_deals_remaining ?? '—')}
                                </td>
                                <td style={tdStyle}>
                                    <input
                                        type="number"
                                        min={0}
                                        defaultValue={u.free_trial_deals_allowed ?? ''}
                                        placeholder={String(defaultFreeDeals)}
                                        disabled={isSubscriberUser(u)}
                                        title={isSubscriberUser(u)
                                            ? 'Subscribers cannot spend leftover trial deals. Use Courtesy Left.'
                                            : undefined}
                                        style={{
                                            width: '64px',
                                            background: isSubscriberUser(u) ? '#f1f5f9' : undefined,
                                        }}
                                        onBlur={(e) => {
                                            if (isSubscriberUser(u)) return;
                                            const val = e.target.value;
                                            if (val !== String(u.free_trial_deals_allowed ?? '')) {
                                                saveUserTrialDeals(u.id, val);
                                            }
                                        }}
                                    />
                                </td>
                                <td style={tdStyle}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                        <input
                                            key={`courtesy-${u.id}-${u.courtesy_free_deals_remaining ?? 0}`}
                                            type="number"
                                            min={0}
                                            defaultValue={u.courtesy_free_deals_remaining ?? 0}
                                            style={{ width: '64px' }}
                                            title="Ad hoc free closes, separate from trial and referral credits"
                                            onBlur={(e) => {
                                                const val = e.target.value === '' ? '0' : e.target.value;
                                                if (val !== String(u.courtesy_free_deals_remaining ?? 0)) {
                                                    saveUserCourtesyDeals(u.id, val);
                                                }
                                            }}
                                        />
                                        <button
                                            type="button"
                                            title="Save courtesy remaining"
                                            onClick={(e) => {
                                                const input = e.currentTarget.previousSibling;
                                                const val = input?.value === '' ? '0' : input.value;
                                                saveUserCourtesyDeals(u.id, val);
                                            }}
                                        >
                                            Set
                                        </button>
                                    </div>
                                </td>
                                <td style={tdStyle}>{u.contracts_count ?? 0}</td>
                                <td style={tdStyle}>{u.listings_without_contract_count ?? 0}</td>
                                <td style={tdStyle}>{u.buyer_agreements_without_contract_count ?? 0}</td>
                                <td style={tdStyle}>{u.total_pipeline ?? 0}</td>
                                <td style={tdStyle}>{u.fee_counts?.tentative ?? 0}</td>
                                <td style={tdStyle}>{u.fee_counts?.recognized ?? 0}</td>
                                <td style={tdStyle}>{u.fee_counts?.scheduled ?? 0}</td>
                                <td style={tdStyle}>{u.fee_counts?.charged ?? 0}</td>
                                <td style={tdStyle}>{u.fee_counts?.cancelled ?? 0}</td>
                                <td style={tdStyle}>{u.fee_counts?.waived ?? 0}</td>
                                <td style={tdStyle}>{u.billing_status || '—'}</td>
                                <td style={tdStyle}>${((u.charges_90d_cents || 0) / 100).toFixed(2)}</td>
                                <td style={tdStyle}>{formatDate(u.next_payment_at)}</td>
                            </tr>
                        ))}
                        {users.length === 0 && !tableLoading && (
                            <tr>
                                <td colSpan={20} style={{ padding: '12px', textAlign: 'center' }}>No users found</td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            <h3 style={{ marginTop: '40px' }}>Referral Credits by User</h3>
            <p style={{ color: '#64748b', fontSize: '0.9rem', marginTop: '-8px', maxWidth: '960px' }}>
                Accrued referral credits from <code>referral_credit_events</code>. Remaining credits waive
                the next subscriber close. This is separate from free-trial allotment and courtesy deals.
                The email filter above applies here too.
            </p>
            <div style={{ overflowX: 'auto', background: '#ffffff', border: '1px solid #e5e7eb', marginBottom: '32px' }}>
                <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0 }}>
                    <thead>
                        <tr>
                            <th style={thStyle}>Email</th>
                            <th style={thStyle}>Payment Type</th>
                            <th style={thStyle}>Signups</th>
                            <th style={thStyle}>People Referred</th>
                            <th style={thStyle}>Credits Earned</th>
                            <th style={thStyle}>Credits Used</th>
                            <th style={thStyle}>Credits Remaining</th>
                            <th style={thStyle}>Pending</th>
                            <th style={thStyle}>Last Credited</th>
                        </tr>
                    </thead>
                    <tbody>
                        {referralSummaries.map((row) => (
                            <tr key={row.id}>
                                <td style={tdStyle}>{row.email}</td>
                                <td style={tdStyle}>{row.payment_user_type || '—'}</td>
                                <td style={tdStyle}>{row.signups ?? 0}</td>
                                <td style={tdStyle}>{row.people_referred ?? 0}</td>
                                <td style={tdStyle}>{row.credits_earned ?? 0}</td>
                                <td style={tdStyle}>{row.credits_used ?? 0}</td>
                                <td style={tdStyle}>{row.credits_remaining ?? 0}</td>
                                <td style={tdStyle}>{row.credits_pending ?? 0}</td>
                                <td style={tdStyle}>{formatDate(row.last_credited_at)}</td>
                            </tr>
                        ))}
                        {referralSummaries.length === 0 && (
                            <tr>
                                <td colSpan={9} style={{ padding: '12px', textAlign: 'center' }}>
                                    No referral activity{emailFilter.trim() ? ' matching this email filter' : ''}.
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            <h3 style={{ marginTop: '40px' }}>User&apos;s Revenue Overview</h3>
            <p style={{ color: '#64748b', fontSize: '0.9rem', marginTop: '-8px' }}>
                Enter a user ID or email to see subscription payments, transaction fees, and pending listings or buyer
                agreements. Rows are sorted by charged date (if any), then created date.
            </p>
            <div style={{ display: 'flex', gap: '8px', marginBottom: '16px', maxWidth: '640px' }}>
                <input
                    value={revenueUserQuery}
                    onChange={(e) => setRevenueUserQuery(e.target.value)}
                    placeholder="User ID or email"
                    style={{ flex: 1 }}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') loadUserRevenue();
                    }}
                />
                <button type="button" onClick={loadUserRevenue} disabled={revenueLoading}>
                    {revenueLoading ? 'Loading…' : 'Load'}
                </button>
            </div>
            {revenueError && (
                <div style={{ color: '#b91c1c', marginBottom: '12px' }}>{revenueError}</div>
            )}
            {revenueOverview && (
                <>
                    <div style={{ marginBottom: '12px', color: '#475569' }}>
                        <strong>{revenueOverview.user?.email}</strong>
                        {' '}(id {revenueOverview.user?.id})
                        {' · '}{revenueOverview.total_pipeline ?? 0} total pipeline
                        {' '}({revenueOverview.contracts_count ?? 0} contracts,{' '}
                        {revenueOverview.listings_without_contract_count ?? 0} listings w/o contract,{' '}
                        {revenueOverview.buyer_agreements_without_contract_count ?? 0} buyer agmts w/o contract)
                    </div>
                    <div style={{ overflowX: 'auto', background: '#ffffff', border: '1px solid #e5e7eb' }}>
                        <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0 }}>
                            <thead>
                                <tr>
                                    <th style={thStyle}>Type</th>
                                    <th style={thStyle}>Description</th>
                                    <th style={thStyle}>Contract</th>
                                    <th style={thStyle}>Listing</th>
                                    <th style={thStyle}>Buyer Agmt</th>
                                    <th style={thStyle}>Created</th>
                                    <th style={thStyle}>Updated</th>
                                    <th style={thStyle}>Charged</th>
                                    <th style={thStyle}>Amount</th>
                                    <th style={thStyle}>Charge</th>
                                    <th style={thStyle}>Status</th>
                                    <th style={thStyle}>Waived</th>
                                </tr>
                            </thead>
                            <tbody>
                                {(revenueOverview.rows || []).map((row, idx) => (
                                    <tr key={`${row.row_type}-${row.transaction_fee_id || row.billing_payment_id || row.listing_id || row.buyer_agreement_id || idx}`}>
                                        <td style={tdStyle}>
                                            {row.row_type === 'subscription_payment'
                                                ? 'Subscription'
                                                : row.row_type === 'listing'
                                                    ? 'Listing'
                                                    : row.row_type === 'buyer_agreement'
                                                        ? 'Buyer Agmt'
                                                        : 'Transaction'}
                                        </td>
                                        <td style={tdStyle}>{row.description}</td>
                                        <td style={tdStyle}>{row.contract_id ?? '—'}</td>
                                        <td style={tdStyle}>{row.listing_id ?? '—'}</td>
                                        <td style={tdStyle}>{row.buyer_agreement_id ?? '—'}</td>
                                        <td style={tdStyle}>{formatDate(row.created_at)}</td>
                                        <td style={tdStyle}>{formatDate(row.updated_at)}</td>
                                        <td style={tdStyle}>{formatDate(row.charged_at)}</td>
                                        <td style={tdStyle}>
                                            {row.amount_cents != null ? centsToDisplay(row.amount_cents) : '—'}
                                        </td>
                                        <td style={tdStyle}>
                                            {row.charge_amount_cents != null
                                                ? centsToDisplay(row.charge_amount_cents)
                                                : (row.amount_cents != null ? centsToDisplay(row.amount_cents) : '—')}
                                        </td>
                                        <td style={tdStyle}>{row.status || '—'}</td>
                                        <td style={tdStyle}>
                                            {row.waived_at_close == null ? '—' : (row.waived_at_close ? 'Yes' : 'No')}
                                        </td>
                                    </tr>
                                ))}
                                {(revenueOverview.rows || []).length === 0 && (
                                    <tr>
                                        <td colSpan={12} style={{ padding: '12px', textAlign: 'center' }}>
                                            No revenue activity for this user.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </>
            )}
        </div>
    );
}

export default DeveloperRevenue;
